/**
 * Heat recipes resource — typed wrappers over `/v1/twin/recipes` plus the
 * co-located React Query hooks (Phase-2 Wave-0 recipe UX; backend migration 0107
 * + internal/twin/recipes.go).
 *
 * A "recipe" is the PHYSICAL selection a heat is run to: WHICH product, on WHICH
 * pattern/tooling, to WHICH grade, in WHAT planned quantity. Deliberately NOT ERP
 * — no stock / price / purchase-order / customer / due-date. Its job: a picked
 * recipe drives the sim pour's per-casting designed weight from the SELECTED
 * pattern's cavities, so "pick a recipe → the castings weigh what that pattern
 * says" holds. The list + create back the Recipes screen; the list also feeds the
 * process-graph Run button's recipe picker.
 *
 * Mirrors the typed-wrapper shape of twin-products.ts / twin-patterns.ts
 * (snake_case wire shapes, `api` client from api-client). IDENTITY: product_id /
 * pattern_id are PICKED from the org's existing products / patterns (never
 * free-typed); the backend re-validates that a supplied id belongs to the org
 * (400 otherwise). Hooks live-gate on the build flag + an authenticated,
 * org-resolved session exactly like the sibling twin-* modules.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";

/** A heat recipe row + the resolved product/pattern display names (joined). */
export interface Recipe {
  id: string;
  org_id: string;
  product_id?: string;
  pattern_id?: string;
  /** Joined product name — undefined when unset or since-deleted. */
  product_name?: string;
  /** Joined pattern display name — undefined when unset or since-deleted. */
  pattern_name?: string;
  grade: string;
  material_class: string;
  planned_qty?: number;
  created_by: string;
  created_at?: string;
}

/** Create body — product_id / pattern_id are optional picks from the org's lists. */
export interface RecipeCreate {
  product_id?: string;
  pattern_id?: string;
  grade: string;
  material_class: string;
  planned_qty?: number;
}

interface RecipeList {
  items: Recipe[];
  total: number;
  limit: number;
  offset: number;
}

export const twinRecipes = {
  /** Every recipe for the active org, newest first (joined to product/pattern names). */
  list: () => api.get<RecipeList>("/v1/twin/recipes"),
  /** Create a recipe. Pick-from-list: a named product/pattern must belong to the org. */
  create: (body: RecipeCreate) => api.post<Recipe>("/v1/twin/recipes", body),
  /**
   * Edit a recipe (PATCH). FULL-REPLACE: an omitted field CLEARS it, so callers
   * must send the whole form. Same body shape as create; same pick-from-list
   * re-validation (400 invalid_product / invalid_pattern). 404 recipe_not_found
   * when the id isn't the org's. Returns the updated Recipe (joined names).
   */
  update: (id: string, body: RecipeCreate) =>
    api.patch<Recipe>(`/v1/twin/recipes/${id}`, body),
  /**
   * Delete a recipe. 204 on success; 404 when the id isn't the org's; 409
   * recipe_in_use (with a human `detail` sentence) when a heat has already been
   * run to it — the caller MUST surface that message, not swallow it.
   */
  remove: (id: string) => api.delete<void>(`/v1/twin/recipes/${id}`),
};

/** Build-time flag: only when explicitly "true" do live fetches fire. */
const isLiveApi = import.meta.env.VITE_USE_LIVE_API === "true";

/**
 * Live fetches require the build flag AND an authenticated, org-resolved
 * session — same gate as the sibling twin-* modules (kept local so this module
 * stays additive and free of a circular import on queries.ts).
 */
function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApi && isAuthenticated && activeOrgId !== null;
}

/** Query key for the recipe list — exported so mutations can invalidate it. */
export const TWIN_RECIPES_KEY = ["twin-recipes"] as const;

/** The active org's recipes (drives the Recipes screen + the Run recipe picker). */
export function useRecipesQuery() {
  return useQuery({
    queryKey: TWIN_RECIPES_KEY,
    queryFn: () => twinRecipes.list(),
    enabled: useLiveEnabled(),
    staleTime: 60_000,
  });
}

/** Create a recipe; invalidates the list on success. */
export function useCreateRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecipeCreate) => twinRecipes.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_RECIPES_KEY });
    },
  });
}

/**
 * Edit a recipe (full-replace PATCH); invalidates the list on success. Takes
 * `{ id, body }` so the caller sends the whole form (an omitted field clears it).
 */
export function useUpdateRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RecipeCreate }) =>
      twinRecipes.update(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_RECIPES_KEY });
    },
  });
}

/**
 * Delete a recipe; invalidates the list on success. A 409 recipe_in_use (a heat
 * already runs to it) rejects with an ApiError whose `detail` is a human sentence —
 * surface it (e.g. via writeErrorMessage), never swallow it.
 */
export function useDeleteRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => twinRecipes.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_RECIPES_KEY });
    },
  });
}
